import { useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/shadcn/dialog'
import { GenerateButton } from '@/components/ui/buttons/generate-button'
import { UploadButton } from '@/components/ui/buttons/uploadu-button'
import { Button } from '@/components/ui/shadcn/button'
import { useTheme } from '@/context/themeContext'
import { cn } from '@/lib/utils'
import { Film, Frame, Sparkles } from 'lucide-react'

type ModalView = 'selection' | 'generate'

type Props = {
  open: boolean
  onUploadClick: () => void
  onGenerateClick: () => void
  onEmptyCanvasClick: () => void
  onGenerateDesignClick?: () => void
  onGenerateVideoClick?: () => void
  hasImages?: boolean
  onClose: () => void
}

export function WorkspaceMediaModal({
  open,
  onUploadClick,
  onGenerateClick,
  onEmptyCanvasClick,
  onGenerateDesignClick,
  onGenerateVideoClick,
  hasImages = false,
  onClose,
}: Props) {
  const { theme } = useTheme()
  const [view, setView] = useState<ModalView>('selection')

  const handleClose = () => {
    setView('selection')
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn('sm:max-w-[500px]', theme === 'dark' ? 'dark' : '')}>
        <DialogTitle className="sr-only">Add media</DialogTitle>
        {view === 'selection' && (
          <SelectionView
            onUpload={() => {
              onUploadClick()
              handleClose()
            }}
            onGenerate={() => {
              onGenerateClick()
              handleClose()
            }}
            onEmptyCanvas={() => {
              onEmptyCanvasClick()
              handleClose()
            }}
            onGenerateDesign={
              onGenerateDesignClick
                ? () => {
                    onGenerateDesignClick()
                    handleClose()
                  }
                : undefined
            }
            onGenerateVideo={
              onGenerateVideoClick
                ? () => {
                    onGenerateVideoClick()
                    handleClose()
                  }
                : undefined
            }
            hasImages={hasImages}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

type SelectionViewProps = {
  onUpload: () => void
  onGenerate: () => void
  onEmptyCanvas: () => void
  onGenerateDesign?: () => void
  onGenerateVideo?: () => void
  hasImages: boolean
}

function SelectionView({
  onUpload,
  onGenerate,
  onEmptyCanvas,
  onGenerateDesign,
  onGenerateVideo,
  hasImages,
}: SelectionViewProps) {
  return (
    <>
      <div className="flex flex-col items-center justify-center gap-6 p-6">
        {onGenerateDesign && (
          <div className="flex flex-col items-center gap-2">
            <Button size="lg" variant="primary" className="rounded-full" onClick={onGenerateDesign}>
              <Sparkles className="mr-2 h-5 w-5" />
              Auto Design
            </Button>
            <p className="text-muted-foreground text-center text-sm">
              AI creates a complete, ready-to-post graphic
            </p>
          </div>
        )}

        <div className="flex flex-col items-center gap-2">
          <GenerateButton
            size="lg"
            className="rounded-full"
            onClick={onGenerate}
            text="Generate Image"
          />
          <p className="text-muted-foreground text-center text-sm">
            Describe your images and let AI do the rest
          </p>
        </div>

        <div className="flex flex-col items-center gap-2">
          <UploadButton size="lg" className="rounded-full" onClick={onUpload} />
          <p className="text-muted-foreground text-center text-sm">
            Upload your image, pdf, video or Drag & Drop your files
          </p>
        </div>

        <div className="flex flex-col items-center gap-2">
          <Button size="lg" variant="secondary" className="rounded-full" onClick={onEmptyCanvas}>
            <Frame className="mr-2 h-5 w-5" />
            Empty Canvas
          </Button>
          <p className="text-muted-foreground text-center text-sm">
            Start with a blank canvas and design from scratch
          </p>
        </div>

        {onGenerateVideo && (
          <div className="flex flex-col items-center gap-2">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full"
              onClick={onGenerateVideo}
              disabled={!hasImages}>
              <Film className="mr-2 h-5 w-5" />
              Generate Slideshow
            </Button>
            <p className="text-muted-foreground text-center text-sm">
              {hasImages
                ? 'Create a slideshow video from your images'
                : 'Add images first to create a slideshow'}
            </p>
          </div>
        )}
      </div>
    </>
  )
}
